import classNames from 'classnames';
import { IconProps } from 'Icons/IconProps';
import * as React from 'react';
import { FlexWrapper } from 'Shared/FlexWrapper';
import styles from '../Styles/button.module.css';

export interface ButtonProps {
    children?: React.ReactNode;
    text?: string;
    callback?: () => void;
    className?: string;
    reset?: boolean;
    fullWidth?: boolean;
    disable?: boolean;
    lowProfile?: boolean;
    inverse?: boolean;
    reverse?: boolean;
    warning?: boolean;
    withMarginTop?: boolean;
    withMarginBottom?: boolean;
    style?: React.CSSProperties;
    styleReset?: boolean;
    type?: 'submit' | 'reset' | 'button';
    Icon?: React.FC<IconProps>;
    tooltip?: string;
}

export const Button: React.FC<ButtonProps> = ({
    children,
    text,
    callback,
    className = '',
    reset = false,
    fullWidth = false,
    disable = false,
    lowProfile = false,
    inverse = false,
    reverse = false,
    warning = false,
    withMarginTop = false,
    withMarginBottom = false,
    style = {},
    type = 'button',
    Icon,
    styleReset = false,
    tooltip,
}) => {
    const [disabled, toggleDisable] = React.useState<boolean>(disable);

    const handleClick = (e: React.SyntheticEvent<HTMLButtonElement>) => {
        if (!disabled) {
            if (type === 'button') {
                e.preventDefault();
            }

            callback?.();
        }
    };

    React.useEffect(() => {
        if (reset) {
            toggleDisable(true);
        } else {
            toggleDisable(disable);
        }
    }, [reset]);

    React.useEffect(() => {
        toggleDisable(disable);
    }, [disable]);

    const buttonStyles = (): string => {
        if (styleReset) return styles.Reset;

        const buttonStyle = [styles.Button];

        if (inverse) buttonStyle.push(styles.InverseButton);
        if (warning) buttonStyle.push(styles.WarningButton);
        if (reverse) buttonStyle.push(styles.ReverseButton);

        return buttonStyle.join(' ');
    };

    const buttonClasses = classNames(buttonStyles(), className);

    return (
        <button
            className={buttonClasses}
            style={{
                marginBottom: withMarginBottom ? '0.75rem' : '0',
                marginTop: withMarginTop ? '0.75rem' : '0',
                padding: lowProfile ? '0.5rem' : '0.75rem',
                width: fullWidth ? '100%' : 'auto',
                ...style,
            }}
            onClick={handleClick}
            disabled={disabled}
            // eslint-disable-next-line react/button-has-type
            type={type}
            data-qa={`button-${text}`}
            title={tooltip}
        >
            {children || (
                <FlexWrapper justify="between" align="center">
                    <span>{text}</span>
                    {Icon && <Icon />}
                </FlexWrapper>
            )}
        </button>
    );
};
